import React, { ChangeEventHandler, useState } from "react";
import { useTranslation } from 'react-i18next';
import { AttentionIcon, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { Destination, RoutingRule, Source } from "../common/ObjectTypes";
import SourceFilter from "../sources/SourcesFilter";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import Spinner from "../common/Spinner";
import { page_size } from "../../api/apiUtils";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "../sources/SourceHelperFunctions";
import { tableClasses, paginationClasses } from "../common/tailwindClassnames";

type ConnectionSourceProps = {
    sources: Source[],
    totalSources: number,
    filters: any[],
    globalSearchStr: string,
    take: number,
    offset: number,
    onPageClicked: Function,
    loadingSources: boolean,
    pageLoaded: boolean,
    destinationUrls: string[],
    onChangeGlobalSourceSearch: Function,
    onChangeSearchUrlFilter: ChangeEventHandler,
    onChangeDestinationUrlSearchBar: ChangeEventHandler,
    onRemoveSourceFilterTag: Function,
    onResetSourceFilters: Function,
    requestSortSources: Function,
    loadingDestinationUrls: boolean,
    loadMoreDestinationUrls: boolean,
    onLoadMoreDestinationUrls: Function
}

const ConnectionDetailsSource = ({
    sources,
    totalSources,
    filters,
    globalSearchStr,
    take,
    offset,
    onPageClicked,
    pageLoaded,
    loadingSources,
    destinationUrls,
    onChangeGlobalSourceSearch,
    onChangeSearchUrlFilter,
    onChangeDestinationUrlSearchBar,
    onRemoveSourceFilterTag,
    onResetSourceFilters,
    requestSortSources,
    loadingDestinationUrls,
    loadMoreDestinationUrls,
    onLoadMoreDestinationUrls
}: ConnectionSourceProps) => {
    const { t } = useTranslation();
    const { timezone } = useTimezone();
    const [sortDirection, setSortDirection] = useState(false);
    const [sortByColumn, setSortByColumn] = useState("external_id");

    return (
        <div className="mt-8">
            {/* div with text and add button */}
            <div className="flex flex-row justify-between items-center w-full">
                <label className="text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("The individual sets of data coming from the provider.")}</label>

            </div>
            {/* div with search bar and table*/}
            <div className="flex flex-row mr-2 my-3">
                <SourceFilter
                    filters={filters}
                    globalSearchStr={globalSearchStr}
                    organizations={[]}
                    providerTypes={[]}
                    destinationTypes={[]}
                    destinationUrls={destinationUrls}
                    onChangeEndpoint={onChangeSearchUrlFilter}
                    onChangeDestinationUrlSearchBar={onChangeDestinationUrlSearchBar}
                    onChangeProviderTypeSearchBar={() => console.log("searching provider type wip")}
                    onChangeGlobalSearch={onChangeGlobalSourceSearch}
                    onRemoveTag={onRemoveSourceFilterTag}
                    onResetFilters={onResetSourceFilters}
                    showOrgFilter={false}
                    showProviderTypeFilter={false}
                    showDestinationTypeFilter={false}
                    loadingDestinationUrls={loadingDestinationUrls}
                    loadMoreDestinationUrls={loadMoreDestinationUrls}
                    onLoadMoreDestinationUrls={onLoadMoreDestinationUrls}
                />
                <div className="w-5/6 my-3">
                    <div className="flex flex-col w-full">
                        <label className="ml-3 text-off-black dark:text-white transition-colors duration-300">{t("Total Sources")}: {totalSources}</label>
                        {sources.length < 1 &&
                            <NoResults
                                headerMsg={t("No Results Found")}
                                labelMsg={t("You may need to configure your first source with this connection.")}
                            />}

                        {sources.length >= 1 &&
                            <table className={`ml-3 mt-3 ${tableClasses.container} cursor-pointer`}>
                                <thead className={tableClasses.header}>
                                    <tr>
                                        <th className={tableClasses.headerCell}>
                                            {t("External ID")}
                                            <span className={tableClasses.sortIcon} onClick={() => {
                                                requestSortSources("external_id", sortDirection)
                                                setSortDirection(!sortDirection)
                                                setSortByColumn("external_id")
                                            }}>
                                                {sortByColumn === "external_id" ?
                                                    sortDirection ? <SortedUp /> : <SortedDown />
                                                    : <SortingArrows />}
                                            </span>
                                        </th>
                                        <th className={tableClasses.headerCell}>
                                            {t("Destination")}
                                        </th>
                                        <th className={tableClasses.headerCell}>
                                            {t("Routes")}
                                        </th>
                                        <th className={tableClasses.headerCell}>
                                            {t("Update Frequency")}
                                        </th>
                                        <th className={tableClasses.headerCell}>
                                            {t("Last Update")}
                                            <span className={tableClasses.sortIcon} onClick={() => {
                                                requestSortSources("last_update", sortDirection)
                                                setSortDirection(!sortDirection)
                                                setSortByColumn("last_update")
                                            }}>
                                                {sortByColumn === "last_update" ?
                                                    sortDirection ? <SortedUp /> : <SortedDown />
                                                    : <SortingArrows />}
                                            </span>
                                        </th>
                                        <th className={tableClasses.headerCell}>
                                            {t("Date Created")}
                                            <span className={tableClasses.sortIcon} onClick={() => {
                                                requestSortSources("created_at", sortDirection)
                                                setSortDirection(!sortDirection)
                                                setSortByColumn("created_at")
                                            }}>
                                                {sortByColumn === "created_at" ?
                                                    sortDirection ? <SortedUp /> : <SortedDown />
                                                    : <SortingArrows />}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                {loadingSources ? <tbody>
                                    <tr>
                                        <td colSpan={6} className={`${tableClasses.cell} text-center`}>
                                            <Spinner />
                                        </td>
                                    </tr>
                                </tbody> : <tbody className="text-sm">
                                    {sources.map((source: Source) => {
                                        return (
                                            <tr key={source.id}
                                                className={tableClasses.row}>
                                                <td className={tableClasses.cell}>
                                                    {source.status === "healthy" ? "" : <AttentionIcon />}{source.external_id}
                                                </td>
                                                <td className={tableClasses.cell}>
                                                    {source.destinations.map((dest: Destination) => {
                                                        let dest_url: string | URL;
                                                        try {
                                                            dest_url = new URL(dest.base_url).hostname
                                                        } catch (err) {
                                                            dest_url = ""
                                                        }
                                                        return (
                                                            <p key={dest.id} className="text-blue-basic hover:text-blue-700 dark:text-dark-blue-300 dark:hover:text-light-blue-300 cursor-pointer no-underline hover:underline transition-colors duration-300">{dest_url}</p>
                                                        )
                                                    })}
                                                </td>
                                                <td className={tableClasses.cell}>
                                                    {source.routing_rules.map((route: RoutingRule) => {
                                                        return (<p key={route.id} className="dark:text-white transition-colors duration-300">{route.name}</p>)
                                                    })}
                                                </td>
                                                <td className={tableClasses.cell}>
                                                    {source.update_frequency}
                                                </td>
                                                <td className={tableClasses.cell}>
                                                    {source.last_update}
                                                </td>
                                                <td className={tableClasses.cell}>
                                                    {formatSourceDatetime(source.created_at, timezone)}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>}
                            </table>}
                        {totalSources > page_size && <Pagination
                            showText={true}
                            handlePageClick={onPageClicked}
                            pageCount={Math.ceil(totalSources / take)}
                            resultStart={offset + 1}
                            resultEnd={(offset + take) < totalSources ? (offset + take) : totalSources}
                            totalCount={totalSources}
                            take={take}
                            loading={!pageLoaded}
                            mainclassname={paginationClasses.container}
                        />}
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ConnectionDetailsSource;